<template>
  <div
    ref="cardVisualBox"
    class="card-visual-box"
    :class="[
      {
        'card-visual-box--border-touch-top-bottom': isTouchingTopBottom,
        'card-visual-box--border-touch-all': isTouchingAllBorders,
        'card-visual-box--no-touch': isNotTouching,
        'card-visual-box--variant-changing': isVariantChanging,
      },
    ]"
    @animationend="resetChangingVariant"
  >
    <div class="card-visual-box__inner">
      <div class="card-visual-box__image-wrapper" :style="`background-color: ${backgroundColor}`">
        <LabelBadge
          class="card-visual-box__badge"
          :productLabels="card.labels || []"
          :productVariantLabels="currentVariant.labels || []"
          :isBackorderable="currentVariant.isBackorderable"
          :backgroundColor="badgeBackgroundColor"
          :badgeSize="isDesktop ? SIZES.normal : SIZES.small"
          :isLastChance="isLastChance"
          :isSoldOut="isSoldOut"
        />
        <transition name="bg-img-fade">
          <video
            v-if="isShowingBackground && backgroundVideoUrl"
            ref="backgroundVideo"
            class="card-visual-box__background-video"
            autoplay="true"
            loop="true"
          >
            <source :src="backgroundVideoUrl" type="video/mp4" />
          </video>
          <ImageContentful
            v-else-if="isShowingBackground"
            class="card-visual-box__background-image"
            :src="backgroundImageUrl"
            containerSelector=".card-visual-box"
            :isBackgroundImage="true"
            :options="{
              imageCompression: 81,
              retinaSupportFactor: 0.5,
              fit: CONTENTFUL_IMAGE_FIT.fill,
            }"
            :alt="cardName"
          />
        </transition>
        <!-- TODO: B2C-2641 - make fixed width and height adaptable -->
        <ImageContentful
          v-show="!isShowingBackground"
          class="card-visual-box__image"
          :class="{
            'card-visual-box__image--greyed-out': isSoldOut,
          }"
          :alt="cardName"
          :isBackgroundImage="true"
          :lazyload="{ rootMargin: '100%' }"
          :options="{
            imageCompression: 91,
            format: imageFormat,
            fixedWidth: 480,
            fixedHeight: 480,
            retinaSupportFactor: 0.25,
            setHeight: false,
            backgroundColor: '#f7f7f7',
          }"
          :src="imageSrc"
        />
      </div>
      <QuickAdd
        v-if="!isRaffleProduct && !isGiftCardProduct && isDesktop"
        class="card-visual-box__quick-add"
        :variant="currentVariant"
        :onSizesPanelChange="onSizesPanelChange"
        :buttonText="$t('filter.quickadd.quickadd')"
        v-data-test-id="'quick-add-button'"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import detectMp4 from '@/helpers/video'
import { POSSIBLE_NO_BORDER_VALUES, PRODUCT_IMAGE_TYPES } from '@/stores/product'
import { determineIfLastChance, determineIfSoldOut } from '@/helpers/product-availabilty.js'
import ImageContentful, {
  CONTENTFUL_IMAGE_FIT,
  CONTENTFUL_IMAGE_FORMAT,
} from '@/components/_shared/ImageContentful.vue'
import LabelBadge from '@/components/_shared/LabelBadge/LabelBadge.vue'
import { SIZES } from '@/components/_shared/Badge/Badge.vue'
import QuickAdd from '@/components/_shared/QuickAdd/QuickAdd.vue'

export default {
  name: 'CardVisualBox',
  components: { ImageContentful, LabelBadge, QuickAdd },

  props: {
    currentVariant: {
      type: Object,
      required: true,
    },
    card: {
      type: Object,
      required: true,
    },
    cardName: {
      type: String,
      required: false,
    },
    isShowingBackground: {
      type: Boolean,
      required: false,
    },
    backgroundColor: {
      type: String,
    },
    badgeBackgroundColor: {
      type: String,
      required: false,
    },
    imageFormat: {
      type: String,
      default: CONTENTFUL_IMAGE_FORMAT.default,
      validator(value) {
        return Object.values(CONTENTFUL_IMAGE_FORMAT).includes(value)
      },
    },
    isGiftCardProduct: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isVariantChanging: false,
      cardVisualBoxWidth: 0,
      CONTENTFUL_IMAGE_FIT,
      SIZES,
    }
  },

  computed: {
    ...mapGetters({
      vpWidth: 'viewport/width',
      isMqLarge: 'viewport/isMqLarge',
    }),

    isTouchingTopBottom() {
      return this.currentImage?.borderTouch === PRODUCT_IMAGE_TYPES.topBottomBorders
    },

    isTouchingAllBorders() {
      return this.currentImage?.borderTouch === PRODUCT_IMAGE_TYPES.allBorders
    },

    isNotTouching() {
      return POSSIBLE_NO_BORDER_VALUES.includes(this.currentImage?.borderTouch)
    },

    backgroundImageUrl() {
      return this.currentVariant.backgroundImageUrl
    },

    backgroundVideoUrl() {
      return this.currentVariant.backgroundVideoUrl
    },

    isRaffleProduct() {
      return this.currentVariant.isRaffle
    },

    isLastChance() {
      return determineIfLastChance(
        this.currentVariant.skus || [],
        this.currentVariant.isBackorderable
      )
    },

    isSoldOut() {
      return determineIfSoldOut(
        this.currentVariant.skus || [],
        this.currentVariant.isRaffle,
        this.currentVariant.hasRaffleEnded
      )
    },

    isDesktop() {
      return this.isMqLarge
    },

    currentImage() {
      return this.currentVariant.productVariantData.assets.find(
        asset => !detectMp4(asset.mediaUrl) && !asset.isEditorialImage
      )
    },

    imageSrc() {
      return this.currentImage?.mediaUrl
    },
  },

  watch: {
    vpWidth: 'updateCardVisualBoxWidth',
    currentVariant() {
      this.isVariantChanging = true
    },
  },

  mounted() {
    this.updateCardVisualBoxWidth()
    this.fetchStoreData()
  },

  methods: {
    ...mapActions({
      fetchStoreData: 'store/fetchData',
    }),

    updateCardVisualBoxWidth() {
      this.cardVisualBoxWidth = this.$refs.cardVisualBox ? this.$refs.cardVisualBox.offsetWidth : 0
    },

    onSizesPanelChange(status) {
      this.sizesPanelIsOpen = status
    },

    resetChangingVariant() {
      this.isVariantChanging = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import './CardVisualBox';
</style>
